<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

const page = usePage().value;
const nuxtApp = useNuxtApp();
const isMobile = ref(false);
const windowWidth = ref(0); // default value
// Filter treks by location

function updateWidth() {
  isMobile.value = window.innerWidth <= 991;
  windowWidth.value = window.innerWidth;
}
onMounted(() => {
  isMobile.value = window.innerWidth <= 991;
  // Access the window object only after the component is mounted
  windowWidth.value = window.innerWidth;
  updateWidth();
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});

interface Trek {
  trekLocation: string;
  isListed: boolean; // Added the missing property
}
const treks = computed(() => {
  return (
    page?.treks?.filter(
      (trek: Trek) =>
        trek?.isListed && trek?.trekLocation === "Worldwide" && trek?.isListed,
    ) ?? []
  );
});
const trekRefs = ref<HTMLElement[]>([]);
function addRef(el: HTMLElement | null, index: number) {
  if (el instanceof HTMLElement) {
    trekRefs.value[index] = el;
  }
}
async function runNavAnimation() {
  if (!process.client) return;

  try {
    const { gsap } = await import("gsap");

    //   console.log("Mounted, total treks:", trekRefs.value.length);
    trekRefs.value.forEach((el, index) => {
      if (!el) {
        console.error("Element not found at index:", index);
        return;
      }

      // console.log("Animating element:", el);
      gsap.fromTo(
        el,
        { opacity: 0, y: 50 }, // Only animate opacity and y position, not scale
        {
          opacity: 1,
          y: 0,
          duration: 1.5, // Shorter duration
          scrollTrigger: {
            trigger: el,
            start: "top 90%", // Adjusted for a later start to smooth into view
            end: "bottom 90%", // Sync end point with start for consistency
            toggleActions: "play none none reverse",
            markers: false,
            scrub: 0.5, // Reduced scrub delay
          },
          ease: "power2.out", // Simpler easing
        },
      );
    });
  } catch (error) {
    console.warn("Navigation animation failed:", error);
  }
}
// On Suspense resolved event
nuxtApp.hook("page:finish", () => {
  setTimeout(() => {
    runNavAnimation();
  }, 750);
});
</script>

<template>
  <section class="treks-nav-wrap">
    <div class="trek worldwide-treks-text" v-html="page?.worldWideTreksText" />
    <div
      v-for="(trek, index) in treks"
      :key="index"
      :ref="(el) => addRef(el, index)"
      class="trek"
    >
      <NuxtLink class="btn from-left" :to="`/${trek?.uri}`">
        <div v-if="trek?.trekDistance" class="trek-stats">
          <p v-if="trek?.trekDistance" class="trek-distance">
            {{ trek?.trekDistance }}
          </p>
          <p v-if="trek?.trekElevation" class="trek-elevation">
            {{ trek?.trekElevation }}
          </p>
        </div>
        <div class="trek-image">
          <NuxtPicture
            v-if="trek?.coverImage && !isMobile"
            format="webp"
            loading="lazy"
            :src="trek?.coverImage.url"
            :width="600"
            :height="500"
            :alt="trek?.coverImage.alt || trek?.title || 'Trek cover image'"
            sizes="sm:100vw md:50vw lg:600px"
          />
          <NuxtPicture
            v-if="trek?.coverImage && isMobile"
            format="webp"
            loading="lazy"
            :src="trek?.coverImage.urlMobile"
            :width="400"
            :height="300"
            :alt="trek?.coverImage.alt || trek?.title || 'Trek cover image'"
            sizes="sm:100vw md:50vw lg:400px"
          />
        </div>
        <div class="trek-title">
          <h3>{{ trek?.title }}</h3>
          <div v-if="trek?.trekGrade === 'Grade 1'" class="trek-grade">
            <AppSVGGradeEasy />
            <p>Easy</p>
          </div>
          <div v-if="trek?.trekGrade === 'Grade 2'" class="trek-grade">
            <AppSVGGradeModerate />
            <p>Moderate</p>
          </div>
          <div v-if="trek?.trekGrade === 'Grade 3'" class="trek-grade">
            <AppSVGGradeDifficult />
            <p>Difficult</p>
          </div>
          <div v-if="trek?.trekGrade === 'Grade 4'" class="trek-grade">
            <AppSVGGradeToughest />
            <p>Toughest</p>
          </div>
        </div>
      </NuxtLink>
    </div>
    <div class="trek where-next">
      <NuxtLink class="btn from-left" to="/worldwide-treks/where-next">
        <div class="trek-image">
          <NuxtPicture
            loading="lazy"
            src="images/where-next-sq.jpg"
            width="600"
            height="500"
            alt="Where next image"
            sizes="sm:200px md:300px lg:400px"
          />
        </div>
        <div class="trek-title">
          <h3>Where next?</h3>
          <div class="trek-grade">
            <AppSVGRegionsIcon />
          </div></div
      ></NuxtLink>
    </div>
  </section>
</template>
